import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useOturum } from './lib/oturum.js';
import { GRUP_ADI, MENU, type MenuOge } from './menu.js';
import { Simge } from './bilesenler/Simge.js';
import { NeroLogo } from './bilesenler/Logo.js';

function grupla(ogeler: MenuOge[]) {
  const gruplar: Array<[MenuOge['grup'], MenuOge[]]> = [];
  for (const o of ogeler) {
    const son = gruplar[gruplar.length - 1];
    if (son && son[0] === o.grup) son[1].push(o);
    else gruplar.push([o.grup, [o]]);
  }
  return gruplar;
}

/**
 * UYGULAMA KABUĞU — MENÜ TEK KAYNAKTAN, HER EKRAN BOYUNDA AYNI.
 *
 * ─── YAŞANAN HATA ───
 * Aynı personel hesabıyla masaüstünde Log Analiz ve Post Studio
 * görünüyor, telefonda görünmüyordu. Sebep izin DEĞİLDİ:
 *   · `menu.ts` tek liste, `izinler.includes(o.izin)` tek süzgeç.
 *   · Ama bu listenin TAMAMI yalnızca yan menüde (`.kenar`) çiziliyordu
 *     ve stil dosyasında `@media (max-width: 860px) { .kenar {
 *     display: none } }` vardı.
 *   · Telefonda geriye tek gezinme kalıyordu: alt çubuk. O da
 *     `altCubuk` işaretli öğeleri alıp `.slice(0, 5)` ile kesiyordu.
 *     İşaret yalnızca Ana Sayfa, İş Emirleri, Müşteriler ve Fiyat
 *     Kataloğu'nda vardı.
 *   · Sonuç: Kasa, Stok, Log Analiz, Post Studio, Yönetici ve Ayarlar
 *     telefonda HİÇBİR bağlantısı olmayan ekranlardı. Rotalar
 *     çalışıyordu — yalnız onlara götüren link yoktu.
 *
 * ─── ÇÖZÜM ───
 * Yan menü telefonda gizlenmiyor, ÇEKMECEYE dönüşüyor: aynı liste, aynı
 * izin süzgeci, aynı gruplama; yalnız sunum değişiyor. Üstteki menü
 * düğmesi açar, perdeye/Esc'e/rota değişimine basınca kapanır. Alt çubuk
 * en sık kullanılan beşin kısayolu olarak kalır.
 *
 * Böylece ileride menüye eklenen her modül telefonda da kendiliğinden
 * görünür — ikinci bir liste bakımı yok.
 * Kilit: packages/web/test/menuPariteMobil.test.js
 */
export function Kabuk({ children }: { children: ReactNode }) {
  const { kullanici, izinler } = useOturum();
  const konum = useLocation();
  const [menuAcik, setMenuAcik] = useState(false);

  /* TEK SÜZGEÇ — masaüstü ve telefon aynı diziden okur. */
  const gorunur = MENU.filter((o) => izinler.includes(o.izin));
  const altCubuk = gorunur.filter((o) => o.altCubuk).slice(0, 5);

  /* Bir bağlantıya basıldığında çekmece kapanmalı; yoksa yeni ekran
     menünün arkasında açılır ve kullanıcı "açılmadı" sanır. */
  useEffect(() => { setMenuAcik(false); }, [konum.pathname]);

  useEffect(() => {
    if (!menuAcik) return undefined;
    const tus = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuAcik(false); };
    window.addEventListener('keydown', tus);
    return () => window.removeEventListener('keydown', tus);
  }, [menuAcik]);

  const bas = kullanici?.ad_soyad?.trim()?.[0]?.toLocaleUpperCase('tr-TR') ?? '?';

  return (
    <div className={`uygulama${menuAcik ? ' menu-acik' : ''}`}>
      {/* Telefon başlığı — masaüstünde hiç çizilmez (CSS'te display:none). */}
      <header className="ust-cubuk">
        <button
          type="button"
          className="menu-dugme"
          aria-label={menuAcik ? 'Menüyü kapat' : 'Menüyü aç'}
          aria-expanded={menuAcik}
          aria-controls="ana-menu"
          onClick={() => setMenuAcik((a) => !a)}
        >
          <Simge ad={menuAcik ? 'carpi' : 'menu'} className="simge" />
        </button>
        <NeroLogo yukseklik={17} />
      </header>

      <nav id="ana-menu" className="kenar" aria-label="Ana menü">
        <div className="kenar-marka">
          <NeroLogo yukseklik={20} />
        </div>

        <div className="kenar-menu">
          {grupla(gorunur).map(([grup, ogeler]) => (
            <div key={grup}>
              <div className="kenar-grup-ad">{GRUP_ADI[grup]}</div>
              {ogeler.map((o) => (
                <NavLink
                  key={o.yol}
                  to={o.yol}
                  end={o.yol === '/'}
                  className={({ isActive }) => `menu-oge ${isActive ? 'etkin' : ''}`}
                >
                  <Simge ad={o.simge} className="simge" />
                  <span>{o.ad}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </div>

        <div className="kenar-alt">
          <NavLink to="/ayarlar" className="kullanici-kutu">
            <span className="harf">{bas}</span>
            <span className="bilgi esnek">
              <span className="isim">{kullanici?.ad_soyad}</span>
              <span className="rol">{kullanici?.rol === 'yonetici' ? 'Yönetici' : 'Personel'}</span>
            </span>
          </NavLink>
        </div>
      </nav>

      {/* Perde: çekmecenin dışına basınca kapanır. Düğme olması
          klavye ve ekran okuyucu için de kapanabilir yapar. */}
      <button
        type="button"
        className="kenar-perde"
        aria-label="Menüyü kapat"
        tabIndex={menuAcik ? 0 : -1}
        onClick={() => setMenuAcik(false)}
      />

      <main className="icerik-alani" key={konum.pathname.split('/')[1]}>
        {children}
      </main>

      <nav className="alt-menu" aria-label="Hızlı menü">
        {altCubuk.map((o) => (
          <NavLink key={o.yol} to={o.yol} end={o.yol === '/'} className={({ isActive }) => (isActive ? 'etkin' : '')}>
            <Simge ad={o.simge} className="simge" />
            <span>{o.kisaAd ?? o.ad}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
